import React, {
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import Icon, { COLORS, ICONS } from '../general-components/icon';
import {
	GetZendeskArticle,
	GetZendeskCategories,
	GetZendeskPost,
	GetZendeskSectionsByCategoryId,
	SearchZendeskArticles,
} from '../../connection/zendesk';
import {
	ZendeskArticle,
	ZendeskCategory,
	ZendeskPost,
	ZendeskSearchArticle,
	ZendeskSection,
	SolutionCenterDrawerStates,
	SolutionCenterViews,
	SolutionCenterContentTypes,
	SolutionCenterContent,
} from '../../types/solution-center';
import { GreenroomState as GS } from '../../stores/types/types';
import { setSolutionCenterContentState, setSolutionCenterDrawerState } from '../../stores/actions/user-interface';
import { META_MENU_ROUTES, META_MENU_STATES } from '../../stores/types/tabs';
import usePrevious from '../../utils/use-previous';
import SearchView from './views/search-view';
import ContentView from './views/content-view';
import CategoryView from './views/category-view';
import SectionView from './views/section-view';
import HomeView from './views/home-view';
import Breadcrumbs from './breadcrumbs';

export default function SolutionCenter() {
	const dispatch = useDispatch();

	const drawerState = useSelector((s: GS) => s.UserInterfaceReducer.solutionCenterDrawerState);
	const view = useSelector((s: GS) => s.UserInterfaceReducer.solutionCenterView);
	const content = useSelector((s: GS) => s.UserInterfaceReducer.solutionCenterContent);
	const metaMenuState = useSelector((s: GS) => s.TabsReducer.metaMenuState);
	const metaMenuActiveRoute = useSelector((s: GS) => s.TabsReducer.metaMenuActiveRoute);
	const rightDrawerOpen = useSelector((s: GS) => s.TabsReducer.rightDrawerOpen);
	const activeTab = useSelector((s: GS) => s.TabsReducer.activeTab);

	// general ui state
	const [transitioning, setTransitioning] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(true);

	const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
	const [selectedSectionId, setSelectedSectionId] = useState<number | null>(null);

	// search state
	const [searchString, setSearchString] = useState<string>('');
	const [currentPage, setCurrentPage] = useState<number>(0);
	const [totalPages, setTotalPages] = useState<number>(0);

	// zendesk state
	const [categories, setCategories] = useState<ZendeskCategory[]>([]);
	const [sections, setSections] = useState<ZendeskSection[]>([]);
	const [searchResults, setSearchResults] = useState<ZendeskSearchArticle[]>([]);
	const [contentResults, setContentResults] = useState<ZendeskArticle | ZendeskPost | null>(null);

	const searchDebounce = useRef<NodeJS.Timeout | null>(null);
	const wrapperRef = useRef<HTMLDivElement>(null);
	const bodyRef = useRef<HTMLDivElement>(null);

	// previous state
	const pMetaMenuState = usePrevious(metaMenuState);
	const pRightDrawerOpen = usePrevious(rightDrawerOpen);
	const pMetaMenuActiveRoute = usePrevious(metaMenuActiveRoute);
	const pActiveTab = usePrevious(activeTab);

	const stylePacksOpen = useMemo(() => {
		const openState = metaMenuState === META_MENU_STATES.OPEN
		|| metaMenuState === META_MENU_STATES.OPENING;
		const stylePacksRouteState = metaMenuActiveRoute === META_MENU_ROUTES.STYLE_PACKS
		|| metaMenuActiveRoute === META_MENU_ROUTES.STYLE_PACK_INFO;
		return openState && stylePacksRouteState;
	}, [metaMenuState, metaMenuActiveRoute]);

	const currentCategory = useMemo(
		() => categories.find((category) => category.id === selectedCategoryId),
		[categories, selectedCategoryId],
	);

	const currentSection = useMemo(
		() => sections.find((section) => section.id === selectedSectionId),
		[sections, selectedSectionId],
	);

	const loadingContent = useMemo(
		() => view === SolutionCenterViews.Content && !contentResults,
		[view, contentResults],
	);

	// reset to default view
	const handleSetHomeView = () => {
		setSelectedCategoryId(null);
		setSelectedSectionId(null);
		dispatch(setSolutionCenterContentState(null));
	};

	const handleSelectCategory = (categoryId?: number) => {
		if (selectedSectionId) setSelectedSectionId(null); // clear section when selecting a category
		if (categoryId) setSelectedCategoryId(categoryId);
		dispatch(setSolutionCenterContentState(null, SolutionCenterViews.Category));
	};

	const handleSelectSection = (sectionId?: number) => {
		if (sectionId) setSelectedSectionId(sectionId);
		dispatch(setSolutionCenterContentState(null, SolutionCenterViews.Section));
	};

	const handleSearch = (searchInput: string) => {
		if (searchDebounce.current) clearTimeout(searchDebounce.current);
		setIsLoading(true);
		setSelectedSectionId(null);
		dispatch(setSolutionCenterContentState(null, SolutionCenterViews.Search));
		searchDebounce.current = setTimeout(() => {
			// zendesk search route will return an error with an empty query
			if (!!searchInput && !!searchInput.trim()) {
				SearchZendeskArticles(searchInput, selectedCategoryId)
					.then((res) => {
						const searchRes: ZendeskSearchArticle[] = [];
						if (typeof res.page === 'number') setCurrentPage(res.page);
						if (typeof res.page_count === 'number') setTotalPages(res.page_count);
						if (res.results && Array.isArray(res.results)) {
							res.results.forEach((result: ZendeskSearchArticle) => {
								const modifiedResult = { ...result };
								// add associated section and category data to the search result
								const section = sections.find((s) => s.id === result.section_id);
								const category = categories.find((c) => c.id === section?.category_id);
								if (section && category) {
									modifiedResult.section_name = section.name;
									modifiedResult.category_name = category.name;
									modifiedResult.category_id = category.id;
									searchRes.push(modifiedResult);
								}
							});
						}
						setSearchResults(searchRes);
					})
					.finally(() => {
						setIsLoading(false);
					});
			} else {
				setSearchResults([]);
				setCurrentPage(0);
				setTotalPages(0);
				setIsLoading(false);
			}
		}, 1000);
	};

	const handleSetContent = async (c: SolutionCenterContent) => {
		setIsLoading(true);
		let newContent: ZendeskArticle | ZendeskPost | null = null;
		if (c.type === SolutionCenterContentTypes.Article) {
			const res = await GetZendeskArticle(c.id);
			if (res.article) {
				// set the selected section and category based on the article
				const section = sections.find((s) => s.id === res.article.section_id);
				if (section) {
					setSelectedSectionId(section.id);
					setSelectedCategoryId(section.category_id);
				}
				newContent = res.article;
			}
		} else if (c.type === SolutionCenterContentTypes.Post) {
			const res = await GetZendeskPost(c.id);
			if (res.post) {
				// currently we are only allowing the direct retrieval of community posts
				setSelectedSectionId(null);
				setSelectedCategoryId(null);
				newContent = res.post;
			}
		}
		setContentResults(newContent);
		setIsLoading(false);
	};

	const handleDrawerState = (state: SolutionCenterDrawerStates) => {
		setTransitioning(true);
		dispatch(setSolutionCenterDrawerState(state));
	};

	const prevDrawerState = usePrevious(drawerState);
	useEffect(() => {
		if (prevDrawerState === SolutionCenterDrawerStates.Open
			&& drawerState === SolutionCenterDrawerStates.Collapsed && !transitioning) {
				// useMetaTabs set drawer to collapsed
				setTransitioning(true);
		}
	}, [drawerState, prevDrawerState]);

	const handleOpenExternalLink = () => {
		let url = 'https://support.brandlive.com/hc/en-us';
		if (view === SolutionCenterViews.Search) {
			url = currentCategory
				? `https://support.brandlive.com/hc/en-us/search?utf8=%E2%9C%93&category=${currentCategory.id}&query=${searchString}`
				: `https://support.brandlive.com/hc/en-us/search?utf8=%E2%9C%93&query=${searchString}`;
		} else if (view === SolutionCenterViews.Category && currentCategory) {
			url = currentCategory.html_url;
		} else if (view === SolutionCenterViews.Section && currentSection) {
			url = currentSection.html_url;
		} else if (view === SolutionCenterViews.Content && contentResults) {
			url = contentResults.html_url;
		}
		window.open(url, '_blank');
	};

	useEffect(() => {
		// load categories and sections on mount
		GetZendeskCategories()
			.then((res) => {
				if (res.categories && Array.isArray(res.categories)) {
					setCategories(res.categories);
					const sectionRes: ZendeskSection[] = [];
					res.categories.forEach((category: ZendeskCategory) => {
						GetZendeskSectionsByCategoryId(category.id).then((_res) => {
							if (_res.sections) {
								sectionRes.push(..._res.sections);
							}
						});
					});
					setSections(sectionRes);
				}
			})
			.finally(() => {
				setIsLoading(false);
			});

		return () => {
			if (searchDebounce.current) clearTimeout(searchDebounce.current);
			dispatch(setSolutionCenterDrawerState(SolutionCenterDrawerStates.Closed));
			dispatch(setSolutionCenterContentState(null));
		};
	}, []);

	useEffect(() => {
		if (searchString) {
			handleSearch(searchString);
		}
	}, [searchString]);

	useEffect(() => {
		if (content && (!contentResults || contentResults.id !== content.id)) {
			handleSetContent(content);
		} else if (!content && contentResults) {
			// if we are not viewing an article, reset the content results
			setContentResults(null);
		}
	}, [content]);

	useEffect(() => {
		if (drawerState !== SolutionCenterDrawerStates.Open) {
			return;
		}
		// collapse the solution center drawer if open and another drawer's state changes
		if (
			(metaMenuState === META_MENU_STATES.OPEN && pMetaMenuState !== META_MENU_STATES.OPEN)
			|| (metaMenuState === META_MENU_STATES.OPENING && pMetaMenuState !== META_MENU_STATES.OPENING)
			|| (metaMenuState === META_MENU_STATES.CLOSING && pMetaMenuState !== META_MENU_STATES.CLOSING)
			|| (metaMenuState === META_MENU_STATES.CLOSED && pMetaMenuState !== META_MENU_STATES.CLOSED)
			|| (rightDrawerOpen && !pRightDrawerOpen)
			|| (metaMenuActiveRoute !== pMetaMenuActiveRoute)
			|| (activeTab !== pActiveTab)
		) {
			handleDrawerState(SolutionCenterDrawerStates.Collapsed);
		}
	}, [metaMenuState, rightDrawerOpen, metaMenuActiveRoute, activeTab]);

	useEffect(() => {
		// reset body scroll when the view changes
		if (bodyRef.current) {
			bodyRef.current.scrollTo({ top: 0, behavior: 'auto' });
		}
	}, [view]);

	return (
		<div
			ref={wrapperRef}
			className={classNames(
				'solution-center-container',
				drawerState,
				transitioning && 'transitioning',
				stylePacksOpen && 'style-packs-open',
			)}
			onTransitionEnd={(e) => {
				if (e.target === wrapperRef.current) {
					setTransitioning(false);
					if (drawerState === SolutionCenterDrawerStates.Closed) {
						dispatch(setSolutionCenterContentState(null));
					}
				}
			}}
		>
			<div className={classNames('sc-tab', view)}>
				{(transitioning || drawerState === SolutionCenterDrawerStates.Open) && (
					<>
						<div className="sc-header">
							<div className="sc-header-left">
								<h6 className="sc-header-text">Brandlive Solution Center</h6>
							</div>
							<div className="sc-header-right">
								<button className="sc-header-btn" onClick={() => handleOpenExternalLink()}>
									<Icon name={ICONS.EXPAND} size={16} color={COLORS.TEXT_GRAY_300} />
								</button>
								<button className="sc-header-btn" onClick={() => handleDrawerState(SolutionCenterDrawerStates.Closed)}>
									<Icon name={ICONS.CLOSE} size={16} color={COLORS.TEXT_GRAY_300} />
								</button>
							</div>
						</div>
						<div
							ref={bodyRef}
							className={classNames(
								'sc-body',
								view === SolutionCenterViews.Search && totalPages > 1 && 'has-pagination',
							)}
						>
							{view === SolutionCenterViews.Home ? (
								<h3 className="sc-h3" style={{ marginBottom: '20px' }}>
									How can we help you?
								</h3>
							) : (
								<Breadcrumbs
									key={`${view}-${selectedCategoryId}-${selectedSectionId}`}
									loadingContent={loadingContent}
									currentCategory={currentCategory}
									currentSection={currentSection}
									handleSetHomeView={handleSetHomeView}
									handleSelectCategory={handleSelectCategory}
									handleSelectSection={handleSelectSection}
								/>
							)}
							{view === SolutionCenterViews.Category && currentCategory && (
								<h3 className="sc-h3 category">
									{currentCategory.name}
								</h3>
							)}
							{!loadingContent && (
								<div className="sc-search-container">
									<input
										className="sc-search-input"
										type="text"
										placeholder="Search"
										onChange={(e) => setSearchString(e.target.value)}
									/>
									<div className="sc-search-icon">
										<Icon name={ICONS.SEARCH} size={16} color={COLORS.TEXT_GRAY_100} />
									</div>
								</div>
							)}
							{view === SolutionCenterViews.Home && (
								<HomeView
									categories={categories}
									handleSelectCategory={handleSelectCategory}
								/>
							)}
							{view === SolutionCenterViews.Category && currentCategory && (
								<CategoryView
									currentCategory={currentCategory}
									sections={sections}
									handleSelectSection={handleSelectSection}
								/>
							)}
							{view === SolutionCenterViews.Section && currentSection && (
								<SectionView
									currentSection={currentSection}
								/>
							)}
							{view === SolutionCenterViews.Search && (
								<SearchView
									isSearching={isLoading}
									searchString={searchString}
									currentPage={currentPage}
									totalPages={totalPages}
									searchResults={searchResults}
									handleSearch={handleSearch}
									handleSelectCategory={handleSelectCategory}
									handleSelectSection={handleSelectSection}
								/>
							)}
							{view === SolutionCenterViews.Content && (
								<ContentView
									isLoading={isLoading}
									contentResults={contentResults}
								/>
							)}
						</div>
					</>
				)}
			</div>
			<button
				className="sc-header-btn collapse-button"
				onClick={() => {
					drawerState === SolutionCenterDrawerStates.Open
						? handleDrawerState(SolutionCenterDrawerStates.Collapsed)
						: handleDrawerState(SolutionCenterDrawerStates.Open);
				}}
				>
				<Icon
					name={
						drawerState === SolutionCenterDrawerStates.Open
							? ICONS.KEYBOARD_ARROW_RIGHT
							: ICONS.KEYBOARD_ARROW_LEFT
					}
					size={16}
					color={COLORS.TEXT_GRAY_300}
				/>
			</button>
		</div>
	);
}
